import React, { useEffect, useRef, useState } from "react";

import CurrencyInput from "./currency-input";

import {
  formatNumberWithSeparators,
  handleNumericKeyDown,
  validateAndLimitInput,
} from "@/features/transactions/utils/budgetInputUtils";

export interface FormattedCurrencyInputProps
  extends Omit<
    React.ComponentProps<typeof CurrencyInput>,
    "value" | "onChange"
  > {
  /**
   * The numeric value of the input. This should be a number (or undefined for empty).
   * The component will format this value with thousand separators for display.
   */
  value?: number;

  /**
   * Callback fired when the numeric value changes.
   * @param value - The new numeric value, or undefined if the input is empty.
   */
  onChange?: (value: number | undefined) => void;

  /**
   * The maximum allowed value. Defaults to 9,999,999.
   * When the user tries to enter a value exceeding this limit, the input will prevent the change.
   */
  maxValue?: number;

  /**
   * Initial value for the input. Used to initialize the display value.
   */
  initialValue?: number;

  /**
   * Whether to allow decimal values. Defaults to false.
   * When true, users can enter decimal numbers (e.g., 123.45).
   */
  decimals?: boolean;
}

/**
 * FormattedNumberInput Component
 *
 * A specialized number input component that:
 * - Formats numbers with thousand separators (e.g., "1.234.567")
 * - Preserves cursor position when editing (especially when deleting digits in the middle)
 * - Prevents input exceeding the maximum value
 * - Only allows numeric input
 * - Handles backspace/delete operations while maintaining cursor position
 *
 * @example
 * ```tsx
 * <FormattedNumberInput
 *   value={budget}
 *   onChange={(value) => setBudget(value)}
 *   maxValue={9999999}
 *   placeholder="0"
 * />
 * ```
 */
export const FormattedCurrencyInput = React.forwardRef<
  HTMLInputElement,
  FormattedCurrencyInputProps
>(
  (
    { value, onChange, maxValue = 9999999, initialValue, decimals = false, onKeyDown, ...props },
    ref
  ) => {
    const [displayValue, setDisplayValue] = useState<string>(() =>
      formatNumberWithSeparators(initialValue ?? value, decimals)
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const cursorPositionRef = useRef<number | null>(null);
    const isInternalUpdateRef = useRef(false);

    // Update display value when external value changes
    useEffect(() => {
      if (!isInternalUpdateRef.current) {
        setDisplayValue(formatNumberWithSeparators(value, decimals));
      }
    }, [value, decimals]);

    // Update display value when initial value changes
    useEffect(() => {
      if (initialValue !== undefined && !isInternalUpdateRef.current) {
        setDisplayValue(formatNumberWithSeparators(initialValue, decimals));
      }
    }, [initialValue, decimals]);

    // Restore cursor position after display value update
    useEffect(() => {
      if (cursorPositionRef.current !== null && inputRef.current) {
        const targetPosition = cursorPositionRef.current;

        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          if (inputRef.current) {
            const newPosition = Math.min(
              targetPosition,
              inputRef.current.value.length
            );
            inputRef.current.setSelectionRange(newPosition, newPosition);
            cursorPositionRef.current = null;
          }
        });
      }
    }, [displayValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const cursorPosition = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? cursorPosition;
      const inputValue = e.target.value;
      const previousValue = displayValue;
      // For decimals, replace comma with dot for parsing, then remove all separators
      // For integers, just remove thousand separators (dots)
      const previousNumericString = decimals
        ? previousValue.replaceAll(".", "").replaceAll(",", ".")
        : previousValue.replaceAll(".", "");

      // Calculate how many numeric digits were before the cursor in the previous value
      const numericBeforeCursor = previousValue
        .slice(0, cursorPosition)
        .replaceAll(".", "")
        .replaceAll(",", "").length;

      // Detect if this was a deletion (value got shorter) or insertion
      const wasDeletion =
        inputValue.length < previousValue.length ||
        selectionEnd - cursorPosition > 0; // Had selection

      const {
        displayValue: formattedValue,
        numericValue,
        shouldPrevent,
      } = validateAndLimitInput(inputValue, maxValue, previousNumericString, decimals);

      // Detect if a comma was just added (for decimal input)
      // This covers both: user typing comma, and dot being converted to comma
      const previousHadComma = decimals && previousValue.includes(",");
      const formattedHasComma = decimals && formattedValue.includes(",");
      const commaJustAdded = decimals && !previousHadComma && formattedHasComma && !wasDeletion;

      // If we should prevent the change, restore previous value and cursor position
      if (shouldPrevent) {
        cursorPositionRef.current = cursorPosition;
        isInternalUpdateRef.current = true;
        setDisplayValue(previousValue);
        // Restore cursor position immediately
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
          isInternalUpdateRef.current = false;
        });
        return;
      }

      // Calculate new cursor position based on numeric position
      let newCursorPosition = 0;
      const newNumericString = decimals
        ? formattedValue.replaceAll(".", "").replaceAll(",", "")
        : formattedValue.replaceAll(".", "");

      // If we have numeric digits before cursor, find the corresponding position
      if (numericBeforeCursor > 0) {
        // For deletions, we want to keep the cursor at the same numeric position
        // For insertions, we want to move forward
        let targetNumericPosition: number;

        // If it was a deletion and we're not at the start, stay at the same position
        // Otherwise, if digits were added, move forward
        const previousNumericLength = decimals
          ? previousNumericString.replaceAll(".", "").replaceAll(",", "").length
          : previousNumericString.length;
        if (
          !wasDeletion &&
          newNumericString.length > previousNumericLength
        ) {
          // A digit was added, move cursor forward by 1
          targetNumericPosition = Math.min(
            numericBeforeCursor + 1,
            newNumericString.length
          );
        } else {
          // Deletion or no change in length - keep at same numeric position
          targetNumericPosition = Math.min(
            numericBeforeCursor,
            newNumericString.length
          );
        }

        // Special handling: if comma was just added (including dot converted to comma), position cursor after the comma
        if (commaJustAdded && formattedValue.includes(",")) {
          const commaIndex = formattedValue.indexOf(",");
          // Position cursor right after the comma
          newCursorPosition = commaIndex + 1;
        }
        // Special handling: when deleting decimals, if we delete the last decimal digit,
        // keep cursor right after comma so next deletion removes the comma
        else if (wasDeletion && decimals && formattedValue.includes(",")) {
          const commaIndex = formattedValue.indexOf(",");
          const decimalPart = formattedValue.substring(commaIndex + 1);
          const hasDecimalDigits = /[0-9]/.test(decimalPart);
          
          // Check if we were deleting from the decimal part
          const previousCommaIndex = previousValue.indexOf(",");
          if (previousCommaIndex !== -1 && cursorPosition > previousCommaIndex) {
            // We were deleting from decimal part
            if (!hasDecimalDigits) {
              // No decimal digits left, keep cursor right after comma
              newCursorPosition = commaIndex + 1;
            } else {
              // Still have decimal digits, calculate position normally but ensure we're in decimal part
              // Find the position in the formatted string that corresponds to this numeric position
              let numericCount = 0;
              for (let i = 0; i < formattedValue.length; i++) {
                if (formattedValue[i] !== "." && formattedValue[i] !== ",") {
                  numericCount++;
                  // Place cursor after the target digit
                  if (numericCount === targetNumericPosition) {
                    newCursorPosition = i + 1;
                    break;
                  }
                }
              }
            }
          } else {
            // Not deleting from decimal part, calculate normally
            let numericCount = 0;
            for (let i = 0; i < formattedValue.length; i++) {
              if (formattedValue[i] !== "." && formattedValue[i] !== ",") {
                numericCount++;
                // Place cursor after the target digit
                if (numericCount === targetNumericPosition) {
                  newCursorPosition = i + 1;
                  break;
                }
              }
            }
          }
        } else {
          // Normal case: find the position in the formatted string that corresponds to this numeric position
          let numericCount = 0;
          for (let i = 0; i < formattedValue.length; i++) {
            if (formattedValue[i] !== "." && formattedValue[i] !== ",") {
              numericCount++;
              // Place cursor after the target digit
              if (numericCount === targetNumericPosition) {
                newCursorPosition = i + 1;
                break;
              }
            }
          }
        }

        // If we didn't find the position (shouldn't happen, but fallback)
        if (newCursorPosition === 0 && formattedValue.length > 0) {
          newCursorPosition = formattedValue.length;
        }
      } else {
        // Cursor was at the start
        // Special handling: if comma was just added (dot converted to comma), position after comma
        if (commaJustAdded && formattedValue.includes(",")) {
          const commaIndex = formattedValue.indexOf(",");
          newCursorPosition = commaIndex + 1;
        }
        // If a digit was added (not deleted), move cursor forward
        else {
          const previousNumericLength = decimals
            ? previousNumericString.replaceAll(".", "").replaceAll(",", "").length
            : previousNumericString.length;
          if (
            !wasDeletion &&
            newNumericString.length > previousNumericLength
          ) {
            // Find the position after the first digit in the formatted string
            let numericCount = 0;
            for (let i = 0; i < formattedValue.length; i++) {
              if (formattedValue[i] !== "." && formattedValue[i] !== ",") {
                numericCount++;
                // Place cursor after the first digit
                if (numericCount === 1) {
                  newCursorPosition = i + 1;
                  break;
                }
              }
            }
            // Fallback: if we didn't find it, move to end
            if (newCursorPosition === 0 && formattedValue.length > 0) {
              newCursorPosition = formattedValue.length;
            }
          } else {
            // Deletion or no change - keep cursor at start
            newCursorPosition = 0;
          }
        }
      }

      cursorPositionRef.current = newCursorPosition;
      isInternalUpdateRef.current = true;
      setDisplayValue(formattedValue);

      onChange?.(numericValue);

      // Reset the flag after a brief delay to allow state update
      requestAnimationFrame(() => {
        isInternalUpdateRef.current = false;
      });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // For decimals, check if we're trying to type more than 2 decimal digits
      if (decimals && /[0-9]/.test(e.key)) {
        const input = e.target as HTMLInputElement;
        const currentValue = input.value;
        const cursorPosition = input.selectionStart ?? 0;
        
        // Check if cursor is after the decimal separator (comma)
        const commaIndex = currentValue.indexOf(",");
        if (commaIndex !== -1 && cursorPosition > commaIndex) {
          // Count decimal digits after comma
          const decimalPart = currentValue.substring(commaIndex + 1);
          const decimalDigits = decimalPart.replaceAll(/[^0-9]/g, "").length;
          
          // Prevent if we already have 2 decimal digits and user is not selecting/replacing
          if (decimalDigits >= 2) {
            const hasSelection = input.selectionStart !== input.selectionEnd;
            // Allow if user is selecting text to replace, or if cursor is in the decimal part being replaced
            if (!hasSelection || input.selectionStart! <= commaIndex) {
              e.preventDefault();
              return;
            }
          }
        }
      }
      
      // Prevent numeric keys if we're already at max length
      if (/[0-9]/.test(e.key)) {
        const currentNumericString = decimals
          ? displayValue.replaceAll(".", "").replaceAll(",", "")
          : displayValue.replaceAll(".", "");
        // For decimals, we need to account for the decimal point in max length
        const maxLength = decimals
          ? maxValue.toString().replaceAll(".", "").length
          : maxValue.toString().length;
        if (currentNumericString.length >= maxLength) {
          // Allow if user is selecting text to replace
          const input = e.target as HTMLInputElement;
          const hasSelection = input.selectionStart !== input.selectionEnd;
          if (!hasSelection) {
            e.preventDefault();
            return;
          }
        }
      }
      handleNumericKeyDown(e, decimals);
      // Call external onKeyDown handler if provided
      onKeyDown?.(e);
    };

    const handleBlur = () => {
      // Format the value on blur to ensure consistent formatting
      if (value !== undefined) {
        setDisplayValue(formatNumberWithSeparators(value, decimals));
        return;
      }
      setDisplayValue("");
    };

    // Merge refs - expose the internal input ref
    useEffect(() => {
      if (typeof ref === "function") {
        ref(inputRef.current);
      } else if (ref) {
        ref.current = inputRef.current;
      }
    }, [ref]);

    return (
      <CurrencyInput
        ref={inputRef}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        {...props}
      />
    );
  }
);

FormattedCurrencyInput.displayName = "FormattedCurrencyInput";